import { useEffect, useRef, useState } from "react";
import { cx, Icon } from "../ui";

// §11.1/D66 — GETEILTE Menüleiste (Shell-Chrome, über ALLE Editoren gleich; Blender/UE5/
// Unity-Stil: Datei/Bearbeiten/Fenster/Hilfe). Sie lebt in der EditorShell, NICHT in einer
// EditorUI — daher „shared". Datengetrieben (Menu[]) ⇒ erweiterbar; das „Fenster"-Menü
// steuert die Panel-Sichtbarkeit (nutzt das Collapse-System der Shell).

export type MenuEntry =
  | { separator: true }
  | { label: string; onClick?: () => void; disabled?: boolean; checked?: boolean; title?: string };

export interface Menu {
  label: string;
  entries: MenuEntry[];
}

export function MenuBar({ menus }: { menus: Menu[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  // Klick/Tap außerhalb schließt; Escape schließt.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="flex items-center gap-0.5">
      {menus.map((m) => (
        <div key={m.label} className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => (o === m.label ? null : m.label))}
            onPointerEnter={() => setOpen((o) => (o ? m.label : o))} // bei offenem Menü per Hover wechseln (DCC-Verhalten)
            className={cx(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              open === m.label ? "bg-surface-2 text-text" : "text-muted hover:text-text",
            )}
          >
            {m.label}
          </button>
          {open === m.label && (
            <div className="absolute left-0 top-full z-50 mt-1 min-w-[210px] rounded-lg border border-border bg-surface p-1 shadow-panel">
              {m.entries.map((e, i) =>
                "separator" in e ? (
                  <div key={`sep-${i}`} className="my-1 h-px bg-border" />
                ) : (
                  <button
                    key={e.label}
                    type="button"
                    disabled={e.disabled}
                    title={e.title}
                    onClick={() => {
                      e.onClick?.();
                      setOpen(null);
                    }}
                    className={cx(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                      e.disabled
                        ? "cursor-not-allowed text-muted/40"
                        : "text-muted hover:bg-surface-2 hover:text-text",
                    )}
                  >
                    <span className="grid w-4 shrink-0 place-items-center">
                      {e.checked ? <Icon name="check" size={13} /> : null}
                    </span>
                    <span className="flex-1">{e.label}</span>
                  </button>
                ),
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
